/**
 * Service-Provider Categories — per-society catalog CRUD.
 *
 * Lightweight table view + add/edit dialog. The API auto-seeds 18 default
 * categories on first GET (plumber, electrician, …) so a brand-new society
 * never sees an empty list.
 *
 * Distinct from Helper Categories (FV kinds for daily domestic help). These
 * categorise externally-contacted local providers (plumbers, tutors, AC techs)
 * residents call directly.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit3, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { CursorPaginator } from '@/components/ui/cursor-paginator';

interface CategoryRow {
  id: string;
  code: string;
  name: string;
  icon_name: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  _count?: { providers: number };
}

interface ListResponse {
  items: CategoryRow[];
  next_cursor: string | null;
}

// Lucide icon picker — short curated set keyed to the default seed
// categories. Free-text fallback supported in the form.
const ICON_OPTIONS: ReadonlyArray<string> = [
  'wrench',
  'plug',
  'air-vent',
  'hammer',
  'paintbrush',
  'sparkles',
  'chef-hat',
  'book-open',
  'heart-pulse',
  'car',
  'bug',
  'brick-wall',
  'wifi',
  'scissors',
  'leaf',
  'spray-can',
  'shirt',
  'package'
];

const CreateSchema = z.object({
  code: z
    .string()
    .trim()
    .min(2, 'Code must be at least 2 chars')
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'lowercase letters / digits / underscore'),
  name: z.string().trim().min(2, 'Name is required').max(80),
  icon_name: z.string().trim().max(40).optional(),
  sort_order: z.coerce.number().int().min(0).max(10_000),
  is_active: z.boolean()
});
type CreateForm = z.infer<typeof CreateSchema>;

const DEFAULT_FORM: CreateForm = {
  code: '',
  name: '',
  icon_name: 'wrench',
  sort_order: 100,
  is_active: true
};

const PAGE_SIZE = 50;

export function ServiceProviderCategoriesPage(): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryRow | null>(null);
  const [confirmDeleteFor, setConfirmDeleteFor] = useState<CategoryRow | null>(null);

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: DEFAULT_FORM,
    mode: 'onChange'
  });

  const [cursor, setCursor] = useState<string | null>(null);
  const [accumulated, setAccumulated] = useState<CategoryRow[]>([]);
  useEffect(() => {
    // Reset accumulated when nothing's loaded
    if (cursor === null) setAccumulated([]);
  }, [cursor]);

  const listQuery = useQuery({
    queryKey: ['service-provider-categories', cursor],
    queryFn: () =>
      get<ListResponse>('/service-provider-categories', {
        params: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) }
      }),
    placeholderData: (prev) => prev
  });

  useEffect(() => {
    if (!listQuery.data) return;
    setAccumulated((prev) => {
      if (cursor === null) return listQuery.data!.items;
      const seen = new Set(prev.map((p) => p.id));
      return [...prev, ...listQuery.data!.items.filter((it) => !seen.has(it.id))];
    });
  }, [listQuery.data, cursor]);

  const items = accumulated;
  const nextCursor = listQuery.data?.next_cursor ?? null;

  const openCreate = (): void => {
    setEditing(null);
    form.reset(DEFAULT_FORM);
    setOpen(true);
  };
  const openEdit = (r: CategoryRow): void => {
    setEditing(r);
    form.reset({
      code: r.code,
      name: r.name,
      icon_name: r.icon_name ?? '',
      sort_order: r.sort_order,
      is_active: r.is_active
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async (v: CreateForm) => {
      if (editing) {
        const { code: _drop, ...rest } = v;
        return patch<CategoryRow>(`/service-provider-categories/${editing.id}`, rest);
      }
      return post<CategoryRow>('/service-provider-categories', v);
    },
    onSuccess: () => {
      toast.success(editing ? 'Category updated' : 'Category added');
      setOpen(false);
      setCursor(null);
      void qc.invalidateQueries({ queryKey: ['service-provider-categories'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<unknown>(`/service-provider-categories/${id}`),
    onSuccess: () => {
      toast.success('Category deleted');
      setConfirmDeleteFor(null);
      setCursor(null);
      void qc.invalidateQueries({ queryKey: ['service-provider-categories'] });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      setConfirmDeleteFor(null);
    }
  });

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Service provider categories"
        description="Curate the categories residents use to find local plumbers, electricians, AC techs and more. The first visit auto-seeds 18 common categories."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add category
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Total</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{items.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Active</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">
              {items.filter((k) => k.is_active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">With providers</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">
              {items.filter((k) => (k._count?.providers ?? 0) > 0).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Disabled</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">
              {items.filter((k) => !k.is_active).length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-vt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="No categories yet"
              description="Add a category to get started, or refresh to auto-seed the defaults."
              action={
                <Button onClick={openCreate}>
                  <Plus className="h-4 w-4" /> Add category
                </Button>
              }
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Icon</TableHead>
                    <TableHead className="text-right">Providers</TableHead>
                    <TableHead className="text-right">Sort</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((k) => (
                    <TableRow key={k.id}>
                      <TableCell className="font-mono text-vt-12">{k.code}</TableCell>
                      <TableCell className="font-medium text-vt-text">{k.name}</TableCell>
                      <TableCell className="font-mono text-vt-12 text-vt-text-muted">
                        {k.icon_name ?? '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-12 text-vt-text-muted">
                        {k._count?.providers ?? 0}
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-12 text-vt-text-muted">
                        {k.sort_order}
                      </TableCell>
                      <TableCell>
                        <Badge variant={k.is_active ? 'ok' : 'muted'}>
                          {k.is_active ? 'Active' : 'Disabled'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-vt-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(k)} title="Edit">
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmDeleteFor(k)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="px-vt-4 pb-vt-3">
                <CursorPaginator
                  hasMore={Boolean(nextCursor)}
                  loading={listQuery.isFetching}
                  itemsShown={items.length}
                  pageSize={PAGE_SIZE}
                  onLoadMore={() => {
                    if (nextCursor) setCursor(nextCursor);
                  }}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : setOpen(false))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit category' : 'Add category'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update name, icon, sort order or visibility.'
                : 'Pick a short lowercase code (e.g. cctv_installer). It cannot be changed later.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="flex flex-col gap-vt-3">
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="spc-code">Code</Label>
                <Input
                  id="spc-code"
                  placeholder="cctv_installer"
                  disabled={Boolean(editing)}
                  {...form.register('code')}
                />
                {form.formState.errors.code ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.code.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="spc-name">Name</Label>
                <Input id="spc-name" placeholder="CCTV installer" {...form.register('name')} />
                {form.formState.errors.name ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.name.message}</p>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="spc-icon">Icon</Label>
              <Select id="spc-icon" {...form.register('icon_name')}>
                <option value="">— none —</option>
                {ICON_OPTIONS.map((ic) => (
                  <option key={ic} value={ic}>
                    {ic}
                  </option>
                ))}
              </Select>
              <p className="text-vt-11 text-vt-text-muted">
                Lucide icon name. Used in resident category pills.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="spc-sort">Sort order</Label>
                <Input
                  id="spc-sort"
                  type="number"
                  min={0}
                  max={10000}
                  {...form.register('sort_order')}
                />
                <p className="text-vt-11 text-vt-text-muted">Lower numbers show first.</p>
              </div>
              <label className="mt-vt-5 flex items-center gap-vt-2 self-end text-vt-13 text-vt-text">
                <input
                  type="checkbox"
                  {...form.register('is_active')}
                  className="h-4 w-4 accent-vt-primary"
                />
                Active (visible to residents)
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={save.isPending}
                loadingText="Saving…"
                disabled={save.isPending || !form.formState.isValid}
              >
                {editing ? 'Save changes' : 'Add category'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={Boolean(confirmDeleteFor)}
        onOpenChange={(v) => (v ? null : setConfirmDeleteFor(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete category?</DialogTitle>
            <DialogDescription>
              {confirmDeleteFor ? (
                <>
                  Delete <span className="font-semibold">{confirmDeleteFor.name}</span> (
                  {confirmDeleteFor.code})? If any provider still references it, the API will
                  reject the delete — deactivate it instead.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setConfirmDeleteFor(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              loadingText="Deleting…"
              onClick={() => confirmDeleteFor && remove.mutate(confirmDeleteFor.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
